import * as React from "react";
import { FunctionComponent } from "react";
import {
  Black,
  Blood,
  Blue,
  Grass,
  GrayDark,
  GrayLight,
  Pink,
  Sky,
  White,
} from "@traceair/tds";
import { style, classes } from "typestyle";

type ButtonType =
  | "primary"
  | "secondary"
  | "control"
  | "success"
  | "danger"
  | "disabled";
type ButtonSize = "big" | "medium";
type ButtonProps = {
  label?: string;
  icon?: JSX.Element;
  onClick?: (ev: any) => void;
  onMouseLeave?: (ev: any) => void;
  onMouseEnter?: (ev: any) => void;
  type?: ButtonType;
  size?: ButtonSize;
  className?: string;
  disabled?: boolean;
};
const Button: FunctionComponent<ButtonProps> = ({
  size,
  type,
  label,
  icon,
  onClick,
  onMouseLeave,
  onMouseEnter,
  className,
  disabled,
}: ButtonProps) => {
  return (
    <button
      onClick={!disabled ? onClick : undefined}
      onMouseLeave={!disabled ? onMouseLeave : undefined}
      onMouseEnter={!disabled ? onMouseEnter : undefined}
      disabled={disabled}
      className={classes(
        style({
          fontSize: size === "big" ? "20px" : "14px",
          fontWeight: 500,
          lineHeight: "24px",
          whiteSpace: "nowrap",
          border: 0,
          cursor: disabled ? "auto" : "pointer",
          minWidth: label ? "72px" : "0px",
          width: size === "big" ? "100%" : "auto",
          height: size === "big" ? "56px" : "auto",
          boxShadow:
            size === "big"
              ? "0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 1px 5px 0 rgba(0, 0, 0, 0.12)"
              : "none",
          padding: "8px 12px",
          borderRadius: "12px",
          background: getBackgroundColorByType(type, disabled),
          color: getTextColorByType(type, disabled),
          fontFamily: "Roboto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          $nest: {
            "&>*:nth-child(2)": {
              // this style will work when both icon and label are visible
              paddingLeft: "6px",
            },
          },
        }),
        className || "",
      )}
    >
      {icon || null}
      {label && <span>{label}</span>}
    </button>
  );
};

export default Button;

function getBackgroundColorByType(type?: ButtonType, disabled?: boolean) {
  if (disabled) {
    return GrayLight;
  }

  switch (type) {
    case "primary":
      return Blue;
    case "success":
      return Grass;
    case "danger":
      return Pink;
    case "control":
      return White;
    case "secondary":
    default:
      return Sky;
  }
}

function getTextColorByType(type?: ButtonType, disabled?: boolean) {
  if (disabled) {
    return GrayDark;
  }

  switch (type) {
    case "primary":
    case "success":
      return White;
    case "control":
      return Black;
    case "danger":
      return Blood;
    case "secondary":
    default:
      return Blue;
  }
}
